import * as React from 'react';
import { useState } from 'react';
import { dataLayerPushAsync } from '../../utils/tracking';

interface PriceListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { businessName: string; email: string; phone: string }) => Promise<void>;
  isSubmitting?: boolean;
  success?: boolean;
}

export function PriceListModal({ isOpen, onClose, onSubmit, isSubmitting = false, success = false }: PriceListModalProps) {
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('El email es obligatorio');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Ingresá un email válido');
      return;
    }

    if (!phone.trim()) {
      setError('El celular es obligatorio');
      return;
    }

    dataLayerPushAsync({
      event: 'price_list_requested',
      email: email.trim(),
      business_name: businessName.trim() || undefined,
    });

    onSubmit({ businessName: businessName.trim(), email: email.trim(), phone: phone.trim() });
  };

  return (
    <div className="pricelist-modal-overlay" onClick={onClose}>
      <style>{`
        .pricelist-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        .pricelist-modal {
          background: #fff;
          border-radius: 16px;
          padding: 32px;
          max-width: 420px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: pricelist-modal-in 0.2s ease-out;
        }
        @keyframes pricelist-modal-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .pricelist-modal-icon {
          font-size: 40px;
          text-align: center;
          margin: 0 0 16px;
        }
        .pricelist-modal h2 {
          font-size: 22px;
          font-weight: 700;
          color: #1a1a1a;
          text-align: center;
          margin: 0 0 8px;
        }
        .pricelist-modal-desc {
          font-size: 15px;
          color: #64748b;
          text-align: center;
          margin: 0 0 24px;
          line-height: 1.5;
        }
        .pricelist-form-group {
          margin: 0 0 16px;
        }
        .pricelist-form-group label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 6px;
        }
        .pricelist-form-group label span {
          color: #9ca3af;
          font-weight: 400;
        }
        .pricelist-form-group input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 15px;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }
        .pricelist-form-group input:focus {
          outline: none;
          border-color: #1EAA25;
          box-shadow: 0 0 0 3px rgba(30, 170, 37, 0.1);
        }
        .pricelist-form-group input::placeholder {
          color: #9ca3af;
        }
        .pricelist-error {
          color: #dc2626;
          font-size: 13px;
          margin: -8px 0 16px;
        }
        .pricelist-submit-btn {
          width: 100%;
          padding: 14px;
          background: #1EAA25;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .pricelist-submit-btn:hover:not(:disabled) {
          background: #178f1e;
        }
        .pricelist-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .pricelist-microcopy {
          font-size: 12px;
          color: #9ca3af;
          text-align: center;
          margin: 16px 0 0;
        }
        .pricelist-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 24px;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          line-height: 1;
        }
        .pricelist-close:hover {
          color: #374151;
        }
        @media (max-width: 480px) {
          .pricelist-modal {
            padding: 24px 20px;
          }
          .pricelist-modal h2 {
            font-size: 20px;
          }
        }
      `}</style>
      <div className="pricelist-modal" onClick={e => e.stopPropagation()}>
        <button className="pricelist-close" onClick={onClose} aria-label="Cerrar">×</button>

        {success ? (
          <>
            <div style={{ textAlign: 'center', margin: '0 0 16px' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" style={{ width: 32, height: 32 }}>
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
            <h2>¡Listo!</h2>
            <p className="pricelist-modal-desc">
              Te enviamos la lista a <strong>{email}</strong>. Revisá tu bandeja de entrada en unos segundos.
            </p>
            <button
              type="button"
              className="pricelist-submit-btn"
              onClick={onClose}
            >
              Cerrar
            </button>
          </>
        ) : (
          <>
            <div className="pricelist-modal-icon">📥</div>
            <h2>Te enviamos la lista completa</h2>
            <p className="pricelist-modal-desc">
              Dejanos tu email y te mandamos la lista mayorista actualizada en segundos.
              Sin compromiso y sin llamados molestos.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="pricelist-form-group">
                <label htmlFor="businessName">
                  Nombre del negocio <span>(opcional)</span>
                </label>
                <input
                  id="businessName"
                  type="text"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  placeholder="Ej: Restaurante La Esquina"
                  disabled={isSubmitting}
                />
              </div>

              <div className="pricelist-form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="pricelist-form-group">
                <label htmlFor="phone">Celular</label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="Ej: 11 2345-6789"
                  disabled={isSubmitting}
                  required
                />
              </div>

              {error && <p className="pricelist-error">{error}</p>}

              <button
                type="submit"
                className="pricelist-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin" /> Enviando...
                  </>
                ) : (
                  'Recibir lista'
                )}
              </button>
            </form>

            <p className="pricelist-microcopy">
              Solo para enviarte la lista. No hacemos spam.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
